import { ChevronRight, Home } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { getBreadcrumbs } from "@/lib/seo";
import { cn } from "@/lib/utils";

interface PageBreadcrumbsProps {
  currentLabel?: string;
  className?: string;
  dark?: boolean;
}

const PageBreadcrumbs = ({
  currentLabel,
  className,
  dark = false,
}: PageBreadcrumbsProps) => {
  const { pathname } = useLocation();
  const breadcrumbs = getBreadcrumbs(pathname, currentLabel);

  return (
    <nav aria-label="Breadcrumb" className={cn("mt-6", className)}>
      <ol
        className={cn(
          "inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium shadow-sm backdrop-blur-md sm:text-sm",
          dark
            ? "border-white/20 bg-slate-950/40 text-slate-300"
            : "border-border/70 bg-white/85 text-muted-foreground",
        )}
      >
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <li key={item.path} className="flex min-w-0 items-center gap-1.5">
              {index > 0 && (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden="true" />
              )}
              {isLast ? (
                <span
                  aria-current="page"
                  className={cn(
                    "max-w-[13rem] truncate sm:max-w-sm",
                    dark ? "text-white" : "text-foreground",
                  )}
                >
                  {index === 0 && <Home className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />}
                  {item.name}
                </span>
              ) : (
                <Link
                  to={item.path}
                  className={cn(
                    "transition-colors",
                    dark ? "hover:text-white" : "hover:text-primary",
                  )}
                >
                  {index === 0 && <Home className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />}
                  {item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default PageBreadcrumbs;
